import { Button, Checkbox, IconButton, SelectInput, SelectInputItem } from '@ads/components-react';
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { EEnrollmentStatus } from '@/shared/api/generated';
import { Textarea } from '@/shared/ui/atoms';

import { useStatusReasons } from '../../../hooks/useStatusReasons';
import { useSubStatusReasons } from '../../../hooks/useSubStatusReasons';
import type { Enrollment, EnrollmentStatus } from '../../../types/enrollment';
import { ENROLLMENT_STATUS_GROUPS } from '../../../types/enrollment';
import { ENROLLMENT_STATUS_TO_CODE } from '../../../types/enrollmentStatusCode';
import { EnrollmentStatusBadge } from '../../atoms/EnrollmentStatusBadge';

// ─── Types ────────────────────────────────────────────────────────────────────

type RequirementLevel = 'ok' | 'warning' | 'blocked' | 'info';

interface TransitionRequirement {
  readonly messageKey: string;
  readonly messageParams?: Record<string, unknown>;
  readonly level: RequirementLevel;
}

interface TransitionConfig {
  readonly needsReason: boolean;
  readonly needsSubReason: boolean;
  readonly needsObservation: boolean;
}

export interface StatusForm {
  readonly newStatus: EnrollmentStatus | '';
  readonly reasonId: string;
  readonly subReasonId: string;
  readonly observation: string;
  readonly sendNotification: boolean;
  readonly sendCertificateByEmail: boolean;
  readonly updateCompanyDiscounts: boolean;
}

/**
 * Props do drawer de mudança de status de matrícula.
 */
export interface EnrollmentStatusDrawerProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly enrollment: Enrollment;
  readonly statusForm: StatusForm;
  readonly onUpdateStatusForm: <K extends keyof StatusForm>(key: K, value: StatusForm[K]) => void;
  readonly onConfirm: () => void;
}

// ─── Transition Matrix ────────────────────────────────────────────────────────

const STATUS_TRANSITIONS: Partial<Record<EnrollmentStatus, readonly EnrollmentStatus[]>> = {
  payment_selection: ['payment_pending', 'canceled_in_registration', 'rejected'],
  payment_pending: ['approval_pending', 'canceled_in_registration', 'rejected'],
  approval_pending: ['approved', 'payment_pending', 'canceled_in_registration', 'rejected'],
  approved: ['activating', 'active', 'canceled', 'blocked'],
  activating: ['active', 'canceled', 'blocked'],
  active: [
    'finished',
    'finished_with_certificate',
    'finished_with_monograph',
    'finished_with_pendencies',
    'canceled',
    'blocked',
    'transferred',
  ],
  finished: ['finished_with_certificate', 'blocked'],
  finished_with_pendencies: ['finished', 'finished_with_certificate', 'blocked'],
  rejected: ['approval_pending'],
  canceled: ['approval_pending'],
  canceled_in_registration: ['approval_pending'],
  blocked: ['active', 'canceled'],
};

const TRANSITION_CONFIG: Record<EnrollmentStatus, TransitionConfig> = {
  payment_selection: {
    needsReason: false,
    needsSubReason: false,
    needsObservation: false,
  },
  payment_pending: {
    needsReason: false,
    needsSubReason: false,
    needsObservation: false,
  },
  approval_pending: {
    needsReason: false,
    needsSubReason: false,
    needsObservation: false,
  },
  approved: {
    needsReason: false,
    needsSubReason: false,
    needsObservation: false,
  },
  activating: {
    needsReason: false,
    needsSubReason: false,
    needsObservation: false,
  },
  active: {
    needsReason: false,
    needsSubReason: false,
    needsObservation: false,
  },
  finished: {
    needsReason: false,
    needsSubReason: false,
    needsObservation: false,
  },
  finished_with_certificate: {
    needsReason: false,
    needsSubReason: false,
    needsObservation: false,
  },
  finished_with_monograph: {
    needsReason: false,
    needsSubReason: false,
    needsObservation: false,
  },
  finished_with_pendencies: {
    needsReason: false,
    needsSubReason: false,
    needsObservation: false,
  },
  rejected: {
    needsReason: true,
    needsSubReason: true,
    needsObservation: true,
  },
  canceled: {
    needsReason: true,
    needsSubReason: true,
    needsObservation: false,
  },
  canceled_in_registration: {
    needsReason: true,
    needsSubReason: true,
    needsObservation: true,
  },
  blocked: {
    needsReason: true,
    needsSubReason: false,
    needsObservation: false,
  },
  transferred: {
    needsReason: false,
    needsSubReason: false,
    needsObservation: false,
  },
  transferred_in_registration: {
    needsReason: false,
    needsSubReason: false,
    needsObservation: false,
  },
};

// ─── Requirement Computation ──────────────────────────────────────────────────

function computeRequirements(
  targetStatus: EnrollmentStatus,
  enrollment: Enrollment,
): readonly TransitionRequirement[] {
  const approvedCount = enrollment.documents.filter(d => d.status === 'approved').length;
  const tempApprovedCount = enrollment.documents.filter(
    d => d.status === 'approved_temporary',
  ).length;
  const pendingCount = enrollment.documents.filter(d => d.status === 'pending').length;
  const rejectedCount = enrollment.documents.filter(d => d.status === 'rejected').length;

  const config = TRANSITION_CONFIG[targetStatus];
  const reqs: TransitionRequirement[] = [];

  if (targetStatus === 'approved' || targetStatus === 'activating' || targetStatus === 'active') {
    const hasPaymentPlan =
      enrollment.financialSummary !== null && enrollment.financialSummary !== undefined;
    reqs.push({
      messageKey: 'statusDrawer.requirements.hasPaymentPlan',
      level: hasPaymentPlan ? 'ok' : 'blocked',
    });

    if (approvedCount > 0 || tempApprovedCount > 0) {
      reqs.push({
        messageKey: 'statusDrawer.requirements.docsApproved',
        messageParams: { approved: approvedCount, temp: tempApprovedCount },
        level: 'ok',
      });
    }

    if (pendingCount > 0) {
      reqs.push({
        messageKey: 'statusDrawer.requirements.docsPending',
        messageParams: { count: pendingCount },
        level: 'warning',
      });
    }

    if (rejectedCount > 0) {
      reqs.push({
        messageKey: 'statusDrawer.requirements.docsRejected',
        messageParams: { count: rejectedCount },
        level: 'blocked',
      });
    }
  }

  if (targetStatus === 'payment_pending' || targetStatus === 'payment_selection') {
    reqs.push({
      messageKey: 'statusDrawer.requirements.hasActivePlan',
      level: 'ok',
    });
    reqs.push({
      messageKey: 'statusDrawer.requirements.courseAllowsRevert',
      level: 'ok',
    });
  }

  if (config.needsReason && config.needsSubReason && config.needsObservation) {
    reqs.push({
      messageKey: 'statusDrawer.formFields.reasonRequired',
      level: 'info',
    });
    reqs.push({
      messageKey: 'statusDrawer.formFields.subReasonRequired',
      level: 'info',
    });
    reqs.push({
      messageKey: 'statusDrawer.formFields.observationRequired',
      level: 'info',
    });
  } else if (config.needsReason && config.needsSubReason) {
    reqs.push({
      messageKey: 'statusDrawer.formFields.reasonRequired',
      level: 'info',
    });
    reqs.push({
      messageKey: 'statusDrawer.formFields.subReasonRequired',
      level: 'info',
    });
  } else if (config.needsReason) {
    reqs.push({
      messageKey: 'statusDrawer.formFields.reasonRequired',
      level: 'info',
    });
  }

  return reqs;
}

// ─── Requirement Item ─────────────────────────────────────────────────────────

function RequirementItem({ req }: { req: TransitionRequirement }) {
  const { t } = useTranslation('personManagement');

  const icons: Record<RequirementLevel, React.ReactNode> = {
    ok: <CheckCircleIcon className="h-xxxs w-xxxs shrink-0 text-base-success-600" />,
    warning: <ExclamationTriangleIcon className="h-xxxs w-xxxs shrink-0 text-base-warning-500" />,
    blocked: (
      <ExclamationCircleIcon className="h-xxxs w-xxxs shrink-0 text-ctx-interactive-destructive" />
    ),
    info: <InformationCircleIcon className="h-xxxs w-xxxs shrink-0 text-ctx-content-placeholder" />,
  };

  return (
    <li className="flex items-start gap-quark">
      {icons[req.level]}
      <span className="ts-caption-xxs text-ctx-content-placeholder">
        {(t as (k: string, o?: Record<string, unknown>) => string)(
          req.messageKey,
          req.messageParams,
        )}
      </span>
    </li>
  );
}

// ─── Status Option Card ───────────────────────────────────────────────────────

interface StatusOptionCardProps {
  readonly targetStatus: EnrollmentStatus;
  readonly requirements: readonly TransitionRequirement[];
  readonly isSelected: boolean;
  readonly onSelect: (status: EnrollmentStatus) => void;
}

function StatusOptionCard({
  targetStatus,
  requirements,
  isSelected,
  onSelect,
}: StatusOptionCardProps) {
  const { t } = useTranslation('personManagement');
  const isBlocked = requirements.some(r => r.level === 'blocked');

  return (
    <button
      type="button"
      onClick={() => !isBlocked && onSelect(targetStatus)}
      className={[
        'w-full rounded-sm border-hairline p-xxs text-left transition-all',
        isSelected
          ? 'border-ctx-interactive-primary bg-ctx-interactive-primary/5'
          : 'border-ctx-layout-border bg-ctx-layout-surface hover:border-ctx-content-placeholder',
        isBlocked ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
      ].join(' ')}
      aria-pressed={isSelected}
    >
      <div className="flex items-start gap-xxs">
        <div
          className={[
            'mt-quark h-[0.875rem] w-[0.875rem] shrink-0 rounded-circular border-thin transition-colors',
            isSelected
              ? 'border-ctx-interactive-primary bg-ctx-interactive-primary'
              : 'border-ctx-content-placeholder',
          ].join(' ')}
        >
          {isSelected && (
            <div className="flex h-full items-center justify-center">
              <div className="h-[0.3rem] w-[0.3rem] rounded-circular bg-white" />
            </div>
          )}
        </div>
        <div className="flex min-w-none flex-1 flex-col gap-quark">
          <span
            className={[
              'ts-paragraph-xs !font-medium',
              isBlocked ? 'text-ctx-content-placeholder' : 'text-ctx-content-title',
            ].join(' ')}
          >
            {t(`enrollmentStatus.${targetStatus}`)}
          </span>

          <span className="ts-caption-xxs text-ctx-content-placeholder">
            {t(`statusDrawer.transitionDescriptions.${targetStatus}`)}
          </span>

          {requirements.length > 0 && (
            <ul className="mt-quark flex flex-col gap-quark">
              {requirements.map(req => (
                <RequirementItem key={req.messageKey} req={req} />
              ))}
            </ul>
          )}
        </div>
      </div>
    </button>
  );
}

// ─── Transition Details ───────────────────────────────────────────────────────

const STATUS_WARNINGS: Partial<
  Record<
    EnrollmentStatus,
    | 'statusDrawer.warnings.approved'
    | 'statusDrawer.warnings.canceled_in_registration'
    | 'statusDrawer.warnings.finished_with_certificate'
    | 'statusDrawer.warnings.blocked'
    | 'statusDrawer.warnings.canceled'
  >
> = {
  approved: 'statusDrawer.warnings.approved',
  canceled_in_registration: 'statusDrawer.warnings.canceled_in_registration',
  finished_with_certificate: 'statusDrawer.warnings.finished_with_certificate',
  blocked: 'statusDrawer.warnings.blocked',
  canceled: 'statusDrawer.warnings.canceled',
};

interface TransitionDetailsProps {
  readonly targetStatus: EnrollmentStatus;
  readonly statusForm: StatusForm;
  readonly onUpdate: <K extends keyof StatusForm>(key: K, value: StatusForm[K]) => void;
}

function TransitionDetails({ targetStatus, statusForm, onUpdate }: TransitionDetailsProps) {
  const { t } = useTranslation('personManagement');
  const config = TRANSITION_CONFIG[targetStatus];
  const targetStatusCode = ENROLLMENT_STATUS_TO_CODE[targetStatus] as EEnrollmentStatus;
  const { reasons } = useStatusReasons(targetStatusCode);
  const { subReasons } = useSubStatusReasons(
    statusForm.reasonId ? Number(statusForm.reasonId) : undefined,
  );
  const warningKey = STATUS_WARNINGS[targetStatus];
  const showCertificateCheckbox =
    targetStatus === 'finished_with_certificate' || targetStatus === 'finished';

  return (
    <div className="flex flex-col gap-xxs rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface p-xxs">
      <span className="ts-paragraph-xs !font-medium text-ctx-content-title">
        {t('statusDrawer.detailsTitle')}{' '}
        <span className="text-ctx-interactive-primary">
          {t(`enrollmentStatus.${targetStatus}`)}
        </span>
      </span>

      {/* Reason + Sub-reason */}
      {config.needsReason && (
        <div className="grid grid-cols-1 gap-xxxs md:grid-cols-2">
          <SelectInput
            label={t('statusDrawer.formFields.reason')}
            value={statusForm.reasonId || 'none'}
            onValueChange={v => onUpdate('reasonId', v === 'none' ? '' : v)}
          >
            <SelectInputItem value="none" custom={{ root: 'text-nowrap' }}>
              {t('enrollmentList.filters.all')}
            </SelectInputItem>
            {reasons.map(r => (
              <SelectInputItem key={r.id} value={r.id} custom={{ root: 'text-nowrap' }}>
                {r.name}
              </SelectInputItem>
            ))}
          </SelectInput>

          {config.needsSubReason && statusForm.reasonId && subReasons.length > 0 && (
            <SelectInput
              label={t('statusDrawer.formFields.subReason')}
              value={statusForm.subReasonId || 'none'}
              onValueChange={v => onUpdate('subReasonId', v === 'none' ? '' : v)}
            >
              <SelectInputItem value="none" custom={{ root: 'text-nowrap' }}>
                {t('enrollmentList.filters.all')}
              </SelectInputItem>
              {subReasons.map(sr => (
                <SelectInputItem key={sr.id} value={sr.id} custom={{ root: 'text-nowrap' }}>
                  {sr.name}
                </SelectInputItem>
              ))}
            </SelectInput>
          )}
        </div>
      )}

      {/* Checkboxes */}
      <div className="flex flex-col gap-nano">
        <Checkbox
          label={t('statusDrawer.formFields.sendNotification')}
          checked={statusForm.sendNotification}
          onCheckedChange={c => {
            if (c !== 'indeterminate') onUpdate('sendNotification', c);
          }}
        />
        <Checkbox
          label={t('statusDrawer.formFields.updateCompanyDiscounts')}
          checked={statusForm.updateCompanyDiscounts}
          onCheckedChange={c => {
            if (c !== 'indeterminate') onUpdate('updateCompanyDiscounts', c);
          }}
        />
        {showCertificateCheckbox && (
          <Checkbox
            label={t('statusDrawer.formFields.sendCertificateByEmail')}
            checked={statusForm.sendCertificateByEmail}
            onCheckedChange={c => {
              if (c !== 'indeterminate') onUpdate('sendCertificateByEmail', c);
            }}
          />
        )}
      </div>

      {/* Observation */}
      <div className="flex flex-col gap-quark">
        <span className="ts-caption-xxs tracking-wider text-ctx-content-placeholder uppercase">
          {t('statusDrawer.formFields.observationLabel')}
        </span>
        <Textarea
          value={statusForm.observation}
          onChange={e => onUpdate('observation', e.target.value)}
          rows={3}
        />
        <span className="ts-caption-xxs text-ctx-content-placeholder">
          {t('statusDrawer.formFields.observationHelper')}
        </span>
      </div>

      {/* Warning */}
      {warningKey && (
        <div className="flex items-start gap-quark rounded-sm bg-base-warning-100 px-xxs py-nano">
          <ExclamationTriangleIcon className="mt-quark h-xxxs w-xxxs shrink-0 text-base-warning-700" />
          <span className="ts-caption-xxs text-base-warning-800">{t(warningKey)}</span>
        </div>
      )}
    </div>
  );
}

// ─── Main Drawer ──────────────────────────────────────────────────────────────

/**
 * Drawer lateral de mudança de status de matrícula.
 * Exibe a matriz de transições disponíveis a partir do status atual,
 * com requisitos dinâmicos e detalhes de cada transição.
 */
export function EnrollmentStatusDrawer({
  isOpen,
  onClose,
  enrollment,
  statusForm,
  onUpdateStatusForm,
  onConfirm,
}: EnrollmentStatusDrawerProps) {
  const { t } = useTranslation('personManagement');

  const availableTargets: readonly EnrollmentStatus[] = STATUS_TRANSITIONS[enrollment.status] ?? [];

  const deniedStatuses: readonly EnrollmentStatus[] = ENROLLMENT_STATUS_GROUPS.denied;

  const isConfirmDisabled =
    !statusForm.newStatus ||
    (deniedStatuses.includes(statusForm.newStatus as EnrollmentStatus) && !statusForm.reasonId);

  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const selectedStatus = statusForm.newStatus as EnrollmentStatus | '';

  return (
    <div className="fixed inset-none z-50 flex">
      {/* Backdrop */}
      <div
        className="absolute inset-none bg-base-neutral-black/medium"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer Panel */}
      <div className="relative ml-auto flex h-full w-full max-w-[32rem] flex-col bg-ctx-layout-surface shadow-level3">
        {/* Header */}
        <div className="flex items-start justify-between border-b-hairline border-ctx-layout-border px-xxs py-xxxs">
          <div className="flex flex-col gap-quark">
            <h2 className="ts-heading-sm text-ctx-content-title">{t('statusDrawer.title')}</h2>
            <p className="ts-paragraph-xs text-ctx-content-placeholder">{enrollment.personName}</p>
          </div>
          <IconButton
            icon={XMarkIcon}
            hierarchy="tertiary"
            size="sm"
            ariaLabel={t('actions.cancel')}
            onClick={onClose}
          />
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-xxs py-xxxs">
          <div className="flex flex-col gap-xxs">
            {/* Current Status */}
            <div className="flex flex-col gap-quark">
              <span className="ts-caption-xxs tracking-wider text-ctx-content-placeholder uppercase">
                {t('statusDrawer.fromLabel')}
              </span>
              <EnrollmentStatusBadge status={enrollment.status} />
            </div>

            {/* Available Transitions */}
            <div className="flex flex-col gap-nano">
              <span className="ts-caption-xxs tracking-wider text-ctx-content-placeholder uppercase">
                {t('statusDrawer.availableTitle', {
                  status: t(`enrollmentStatus.${enrollment.status}`),
                })}
              </span>

              {availableTargets.length === 0 ? (
                <p className="ts-paragraph-xs text-ctx-content-placeholder">
                  {t('statusDrawer.noTransitions')}
                </p>
              ) : (
                <div className="flex flex-col gap-nano">
                  {availableTargets.map(target => {
                    const reqs = computeRequirements(target, enrollment);
                    return (
                      <StatusOptionCard
                        key={target}
                        targetStatus={target}
                        requirements={reqs}
                        isSelected={selectedStatus === target}
                        onSelect={s => onUpdateStatusForm('newStatus', s)}
                      />
                    );
                  })}
                </div>
              )}
            </div>

            {/* Transition Details (shown when a target is selected) */}
            {selectedStatus !== '' && (
              <TransitionDetails
                targetStatus={selectedStatus}
                statusForm={statusForm}
                onUpdate={onUpdateStatusForm}
              />
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-xxs border-t-hairline border-ctx-layout-border px-xxs py-xxxs">
          <Button hierarchy="secondary" size="sm" onClick={onClose}>
            {t('statusDrawer.cancelButton')}
          </Button>
          <Button size="sm" onClick={onConfirm} disabled={isConfirmDisabled}>
            {t('statusDrawer.confirmButton')}
          </Button>
        </div>
      </div>
    </div>
  );
}
